import { action, computed, observable, ObservableMap, transaction } from 'mobx'
import range from 'lodash/range'
import { computedFn } from 'mobx-utils'
import times from 'lodash/times'
import IdObjectStore from './IdObjectStore'
import { forEach } from 'lodash'

export class IndexObjectStore extends IdObjectStore {
  @observable indexObjectMap = new ObservableMap()
  @observable objectLoadingProgress = observable([])
  @observable end = 0

  @computed
  get objects() {
    const result = []
    // for (let i = 0; i < this.indexObjectMap.size; i++) {
    //   result.push(this.indexObjectMap.get(i))
    // }
    const keys = Array.from(this.indexObjectMap.keys())
    keys.forEach(key => result.push(this.indexObjectMap.get(key)))
    return result
  }

  @action
  setEnd = (end: number) => {
    this.end = end
  }

  @action
  setDataByRange = (fromIndex: number, toIndex: number, data: any[]) => {
    const tempIdObjectMap = new Map()
    const tempIndexObjectMap = new Map()
    data.forEach((obj, index) => {
      tempIdObjectMap.set(obj[this.keyField], obj)
      tempIndexObjectMap.set(fromIndex + index, obj)
    })
    this.indexObjectMap.merge(tempIndexObjectMap)
    this.idObjectMap.merge(tempIdObjectMap)
  }

  getIndexByKey = computedFn(id => {
    const iterator = this.indexObjectMap.entries()
    let nextRes = iterator.next()
    while (!nextRes.done) {
      if (nextRes.value?.[1]?.[this.keyField] === id) {
        return +nextRes.value[0]
      }
      nextRes = iterator.next()
    }
    return -1
  })

  @action
  shift = (from: number, to: number) => {
    const tempSiftedObjectMap = new Map()
    for (let i = 0; i < to + this.indexObjectMap.size; i++) {
      if (i >= from && i < to) {
        tempSiftedObjectMap.set(i, null)
      } else {
        tempSiftedObjectMap.set(i, this.indexObjectMap.get(i - to))
      }
    }

    this.indexObjectMap.replace(tempSiftedObjectMap)
  }

  getKeyByIndex = computedFn((index: number) => {
    return this.indexObjectMap.get(index)?.[this.keyField]
  })

  @action
  clearDataByRange = (fromIndex: number, toIndex: number) => {
    const indexes = range(fromIndex, toIndex + 1)
    const ids = indexes.map(index => this.indexObjectMap.get(index)[this.keyField])
    transaction(() => {
      ids.forEach(id => this.idObjectMap.delete(id))
      // Shift indexes
      const keys = Array.from(this.indexObjectMap.keys())
      const lastKey = Math.max(...keys)
      range(toIndex, lastKey).forEach(index => {
        this.indexObjectMap.set(index, this.indexObjectMap.get(index + 1))
      })
      // Delete last index
      times(toIndex - fromIndex + 1).forEach(index => {
        this.indexObjectMap.delete(this.indexObjectMap.size - 1)
      })
      this.objectLoadingProgress.splice(fromIndex, toIndex - fromIndex + 1)
    })
  }

  @action
  clearDataAtIndex = (index: number) => {
    this.clearDataByRange(index, index)
  }

  @action
  setRangeLoading = (
    fromIndex: number,
    toIndex: number,
    loaderKey: string,
    isLoading: boolean
  ) => {
    if (this.objectLoadingProgress.length <= toIndex) {
      this.objectLoadingProgress.splice(
        this.objectLoadingProgress.length,
        toIndex,
        ...Array(toIndex - this.objectLoadingProgress.length).fill(new Map())
      )
    }
    for (let curr = fromIndex; curr < toIndex; curr++) {
      this.objectLoadingProgress[curr] = (
        this.objectLoadingProgress[curr] || new Map()
      ).set(loaderKey, isLoading)
    }
  }

  @action setRangeLoadingForMultipleKeys = (
    fromIndex: number,
    toIndex: number,
    loaderKeys: String[],
    isLoading: boolean
  ) => {
    if (this.objectLoadingProgress.length <= toIndex) {
      this.objectLoadingProgress.splice(
        this.objectLoadingProgress.length,
        toIndex,
        ...Array(toIndex - this.objectLoadingProgress.length).fill(new Map())
      )
    }
    for (let curr = fromIndex; curr < toIndex; curr++) {
      forEach(loaderKeys, loaderKey => {
        ;(this.objectLoadingProgress[curr] || new Map()).set(loaderKey, isLoading)
      })

      this.objectLoadingProgress[curr] = this.objectLoadingProgress[curr] || new Map()
    }
  }

  @action
  setObjectLoading = (index: number, loaderKey: string, isLoading: boolean) => {
    this.setRangeLoading(index, index + 1, loaderKey, isLoading)
  }

  /**
   * This method will check if data is loading or not based on range
   * @param fromIndex
   * @param toIndex
   */
  isObjectsDataLoading = computedFn(
    (fromIndex: number, toIndex: number, loaderKey: string) => {
      return this.objectLoadingProgress
        .slice(fromIndex, toIndex)
        .some(map => map.get(loaderKey))
    }
  )

  isObjectDataLoading = computedFn((index: number, loaderKey: string) => {
    return this.isObjectsDataLoading(index, index + 1, loaderKey)
  })

  @action
  clearAll() {
    super.clearAll()
    this.indexObjectMap.replace(new Map())
    this.objectLoadingProgress.replace([])
  }

  /**
   * This method will check if products data is present or not based on range
   * @param fromIndex
   * @param toIndex
   */
  hasObjectsInRange = computedFn((fromIndex: number, toIndex: number) => {
    return range(fromIndex, toIndex).every(val => this.indexObjectMap.get(val))
  })
}
